import { projectItemHref, projectTabHref } from '@/features/projects/projectLinks'
import type { Notification } from '@/types/notifications'

/**
 * Hvor en besked fører hen.
 *
 * Linket bygges af emnetypen og id'et og ikke af en URL fra serveren. Serveren
 * kender ikke frontendens ruter, og en gemt URL ville pege forkert i det
 * øjeblik, en side blev flyttet — beskeder ligger i indbakken i uger.
 *
 * Stien lægges af `projectItemHref` og ikke i hånden. Den blev skrevet i hånden
 * før, med et `?tab=`, som ingen side læste: hver eneste besked landede på
 * projektets forside, uanset hvad den handlede om.
 *
 * Returnerer null, når der ikke er noget at åbne. Beskeden markeres stadig som
 * læst; den er læst, uanset om der var et sted at gå hen.
 */
export function notificationHref(notification: Notification): string | null {
  const { targetType, targetId, projectId } = notification

  if (!targetType || !targetId) {
    return projectId ? `/app/projects/${projectId}` : null
  }

  if (targetType === 'Project') {
    return `/app/projects/${targetId}`
  }

  if (targetType === 'Invitation') {
    return `/invitationer/${targetId}`
  }

  // Resten hører til inde i en sag. Uden den kan linket ikke bygges, og så er
  // projektets forside stadig bedre end en død URL.
  if (!projectId) {
    return null
  }

  switch (targetType) {
    case 'Document':
      return projectItemHref(projectId, 'documents', 'documentId', targetId)
    case 'Drawing':
      return projectItemHref(projectId, 'drawings', 'drawingId', targetId)
    case 'CommentThread':
      return projectItemHref(projectId, 'comments', 'threadId', targetId)
    case 'Task':
      return projectItemHref(projectId, 'tasks', 'taskId', targetId)
    case 'Defect':
      return projectItemHref(projectId, 'quality', 'defectId', targetId)
    case 'Checklist':
      return projectItemHref(projectId, 'quality', 'checklistId', targetId)
    case 'Tender':
      return projectItemHref(projectId, 'tenders', 'tenderId', targetId)

    // Driftfanen har ingen dialog at åbne for hverken et besøg eller en
    // garanti — de står begge på fanens overblik, som er der, linket lander.
    case 'ServiceVisit':
    case 'Warranty':
      return projectTabHref(projectId, 'operations')

    default:
      return `/app/projects/${projectId}`
  }
}
